import React, { useState, useContext } from 'react';
import moment from 'moment';
import LoadingGIF from '../../../assets/loadingIn.gif';
import { ThemeContext } from '../../../contexts/themeContext';

const getPaymentTypeColor = (payType) => {
  const normalized = (payType || 'cash').toLowerCase();
  switch (normalized) {
    case 'cash':
      return '#39cc6c';
    case 'card':
      return '#03a5fc';
    case 'none':
    case 'ssr':
      return '#5b69bc';
    case 'multiple':
      return '#f39c12';
    case 'complimentary':
      return '#e83e8c';
    default:
      return '#17a2b8';
  }
};

const SubHeader = ({ modalTab, orderDetails, countryDetails }) => {
  if (modalTab === 'crew') return null;

  const symbol = countryDetails?.currency?.symbol || '₹';
  const sales = orderDetails?.employeeSalesSummary;
  const isEmp = !!orderDetails?.employee;

  return (
    <div className="flexCol gap10 width100" style={{ padding: '12px 16px', borderRadius: '6px', fontSize: '0.85rem' }}>
      <div className="flexRow width100 justifyContentSpaceBetween alignItemsCenter">
        <div>
          <span className="margRight10">Showing Data For:</span>
          <span className="fontExtrabold" style={{ fontSize: '0.9rem' }}>
            {isEmp
              ? `${orderDetails.employee.name} (${orderDetails.employee.employeeCode})`
              : 'All Crew (Flight Sector)'}
          </span>
        </div>
        <div className="flexRow gap10" style={{ fontSize: '0.85rem' }}>
          {modalTab === 'orders' && (
            <span>
              <span style={{ color: '#aaa' }}>Total Orders: </span>
              <strong style={{ color: '#17a2b8' }}>{sales?.totalOrders || orderDetails?.orders?.length || 0}</strong>
            </span>
          )}
          {modalTab === 'items' && (
            <span>
              <span style={{ color: '#aaa' }}>Items Sold: </span>
              <strong style={{ color: '#39cc6c' }}>{orderDetails?.itemsSold?.reduce((a, b) => a + (b.totalQuantity || 0), 0) || 0}</strong>
            </span>
          )}
        </div>
      </div>

      {modalTab === 'orders' && (
        <div className="flexRow gap20" style={{ paddingTop: '8px', fontSize: '0.8rem' }}>
          <span>
            <span>{isEmp ? 'Employee BoB Sales: ' : 'Flight BoB Sales: '}</span>
            <strong style={{ color: '#39cc6c' }}>{symbol} {(sales?.totalBoBSales || 0).toFixed(2)}</strong>
          </span>
          <span>
            <span>Cash Sales: </span>
            <strong style={{ color: '#17a2b8' }}>{symbol} {(sales?.totalCashSales || 0).toFixed(2)}</strong>
          </span>
          <span>
            <span>Card Sales: </span>
            <strong style={{ color: '#17a2b8' }}>{symbol} {(sales?.totalCardSales || 0).toFixed(2)}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

const maskCardNumber = (cardNumber) => {
  if (!cardNumber || cardNumber === '-') return '-';
  const str = String(cardNumber).trim();
  if (str.length <= 4 || str.includes('*') || str.includes('•')) return str;
  return `•••• •••• •••• ${str.slice(-4)}`;
};

const CardPaymentDetails = ({ cardPayments }) => {
  if (!cardPayments || cardPayments.length === 0) return null;

  return (
    <div>
      <label className="fontExtrabold inlBlock" style={{ marginBottom: '6px' }}>Card Payment Details</label>
      <div className="flexCol gap10">
        {cardPayments.map((pmt, pIdx) => {
          const isSuccess = (pmt.status || '').toLowerCase() === 'success';
          return (
            <div key={pIdx} className="flexCol" style={{
              backgroundColor: 'rgba(3, 165, 252, 0.08)',
              border: '1px solid rgba(3, 165, 252, 0.2)',
              borderRadius: '6px',
              padding: '10px 12px',
              gap: '6px',
              fontSize: '0.83rem'
            }}>
              <div className="flexRow width100 justifyContentSpaceBetween alignItemsCenter">
                <span style={{ color: '#aaa' }}>Status:</span>
                <span style={{
                  padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                  backgroundColor: isSuccess ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)',
                  color: isSuccess ? '#28a745' : '#dc3545',
                  border: `1px solid ${isSuccess ? '#28a745' : '#dc3545'}`
                }}>
                  {pmt.status || '-'}
                </span>
              </div>
              <div className="flexRow width100 justifyContentSpaceBetween gap10">
                <span style={{ color: '#aaa', whiteSpace: 'nowrap' }}>Invoice No:</span>
                <span style={{ fontWeight: 500, wordBreak: 'break-all', textAlign: 'right' }}>{pmt.invoiceNo || '-'}</span>
              </div>
              <div className="flexRow width100 justifyContentSpaceBetween">
                <span style={{ color: '#aaa' }}>Card Number:</span>
                <span style={{ fontWeight: 500 }}>{maskCardNumber(pmt.cardNumber)}</span>
              </div>
              <div className="flexRow width100 justifyContentSpaceBetween">
                <span style={{ color: '#aaa' }}>Timestamp:</span>
                <span>{pmt.timestamp ? moment(pmt.timestamp).format('DD-MM-YYYY hh:mm A') : '-'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ExpandedOrderDetails = ({ ord, orderDetails, currency, cardPayments, themedColor }) => {
  const itemsList = ord.orderItems || ord.items || [];
  const grandTotal = ord.totalAmountInUsedCurrency ?? ord.totalAmount ?? 0;

  const rawGst = ord.gstAmountInUsedCurrency ?? ord.gstAmount ?? ord.gst;
  const gstAmt = rawGst !== undefined && rawGst !== null
    ? rawGst
    : itemsList.reduce((acc, i) => {
      const itemGst = i.gstAmountInUsedCurrency ?? i.gstAmount ?? i.gst ?? ((i.cgst || 0) + (i.sgst || 0) + (i.igst || 0) + (i.utgst || 0));
      return acc + (itemGst || 0);
    }, 0);

  const discountAmt = ord.discountAmountInUsedCurrency ?? ord.discountAmount ?? ord.discount ?? 0;

  const rawNet = ord.netAmountInUsedCurrency ?? ord.netAmount;
  const grossAmt = rawNet !== undefined && rawNet !== null
    ? rawNet
    : (itemsList.length > 0 && itemsList.some(i => i.netAmount !== undefined || i.netAmountInUsedCurrency !== undefined))
      ? itemsList.reduce((acc, i) => acc + (i.netAmountInUsedCurrency ?? i.netAmount ?? ((i.totalAmount || 0) - (i.gstAmount || i.gst || 0))), 0)
      : (grandTotal - gstAmt);

  return (
    <tr>
      <td style={{ backgroundColor: themedColor.hover || 'rgba(255, 255, 255, 0.05)', padding: '15px', borderBottom: '1px solid rgba(255,255,255,0.08)' }} colSpan="8">
        <div className="width100 justifyContentSpaceBetween display" style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
          {/* LEFT COLUMN: Date & Time, Taken By, Customer Info, Card Payment Details */}
          <div className="flexCol gap10" style={{ width: '50%' }}>
            <div>
              <label className="fontExtrabold inlBlock" style={{ marginBottom: '3px' }}>Date & Time</label>
              <div>
                {ord.timestampDateTime || ord.timestamp ? moment(ord.timestampDateTime || ord.timestamp).format('DD-MM-YYYY hh:mm A') : '-'}
              </div>
            </div>

            <div>
              <label className="fontExtrabold inlBlock" style={{ marginBottom: '3px' }}>Taken By</label>
              <div className="flexCol">
                <div>{ord.createdBy?.employeeName || orderDetails?.employee?.name || '-'}</div>
                <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{ord.createdBy?.employeeCode || orderDetails?.employee?.employeeCode || '-'}</div>
              </div>
            </div>

            <div>
              <label className="fontExtrabold inlBlock" style={{ marginBottom: '3px' }}>Customer Info</label>
              <div className="flexCol">
                <div>{ord.bookingInfo?.customerName || ord.bookingInfo?.passengerName || ord.customerInfo?.customerName || '-'}</div>
                <div>Seat No: {ord.bookingInfo?.seatNumber || ord.customerInfo?.seatNumber || '-'}</div>
                <div>PNR: {ord.bookingInfo?.pnr || ord.customerInfo?.pnr || '-'}</div>
              </div>
            </div>

            <CardPaymentDetails cardPayments={cardPayments} />
          </div>

          {/* RIGHT COLUMN: Items List, Gross, GST, Grand Total */}
          <div className="flexCol gap10" style={{ width: '50%' }}>
            <div>
              <label className="fontExtrabold inlBlock" style={{ marginBottom: '8px' }}>Items</label>
              {itemsList.map((it, itemIdx) => {
                const qty = it.quantity || 1;
                const price = it.itemPrice || it.unitPrice || 0;
                const itemTotal = it.totalAmountInUsedCurrency || it.totalAmount || (qty * price);
                return (
                  <div key={itemIdx} className="flexRow width100 justifyContentSpaceBetween margBottom10" style={{ fontSize: '0.85rem' }}>
                    <div style={{ width: '75%' }}>
                      {it.itemName || it.itemCode || '-'}
                      {it?.sourceSeat?.seatNumber && it?.targetSeat?.seatNumber ? ` (${it.sourceSeat.seatNumber} -> ${it.targetSeat.seatNumber})` : ''}
                      <span className="margLeft10" style={{ color: '#aaa' }}>{qty} x {price}</span>
                    </div>
                    <div>{currency} {Number(itemTotal).toFixed(2)}</div>
                  </div>
                );
              })}

              <div className="flexCol alignItemsEnd" style={{ marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
                <div className="flexRow width100 justifyContentSpaceBetween margBottom10" style={{ maxWidth: '220px' }}>
                  <span>Gross</span>
                  <span className="fontExtrabold">{currency} {Number(grossAmt).toFixed(2)}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="flexRow width100 justifyContentSpaceBetween margBottom10" style={{ maxWidth: '220px' }}>
                    <span>Discount</span>
                    <span className="fontExtrabold" style={{ color: '#f39c12' }}>- {currency} {Number(discountAmt).toFixed(2)}</span>
                  </div>
                )}
                <div className="flexRow width100 justifyContentSpaceBetween margBottom10" style={{ maxWidth: '220px' }}>
                  <span>GST</span>
                  <span className="fontExtrabold">{currency} {Number(gstAmt).toFixed(2)}</span>
                </div>
                <div className="flexRow width100 justifyContentSpaceBetween" style={{ maxWidth: '220px', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
                  <span className="fontExtrabold">Grand Total</span>
                  <span className="fontExtrabold" style={{ color: '#39cc6c' }}>{currency} {Number(grandTotal).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

const OrderRow = ({ ord, idx, isExpanded, setExpandedOrderNo, orderDetails, countryDetails, themedColor }) => {
  const itemsList = ord.orderItems || ord.items || [];
  const itemsCount = itemsList.reduce((acc, i) => acc + (i.quantity || 1), 0);
  const currency = countryDetails?.currency?.symbol || '₹';
  const grandTotal = ord.totalAmountInUsedCurrency ?? ord.totalAmount ?? 0;
  const payType = ord.paymentType || 'Cash';

  let cardPayments = [];
  if (Array.isArray(ord.payments) && ord.payments.length > 0) {
    cardPayments = ord.payments.filter(p => (p.paymentType || '').toLowerCase() === 'card');
  } else if (payType.toLowerCase() === 'card') {
    cardPayments = [{
      status: ord.paymentStatus || ord.status || 'success',
      invoiceNo: ord.invoiceNo || '-',
      cardNumber: ord.cardNumber || '-',
      timestamp: ord.timestampDateTime || ord.timestamp
    }];
  }

  const handleToggle = () => setExpandedOrderNo(isExpanded ? null : (ord.orderNo || ord.id));

  return (
    <React.Fragment key={idx}>
      <tr
        className="pointer"
        style={{
          cursor: 'pointer',
          backgroundColor: isExpanded ? (themedColor.hover || 'rgba(255, 255, 255, 0.05)') : '',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
        onClick={handleToggle}
      >
        <td style={{ padding: '10px 12px', textAlign: 'center', color: '#17a2b8', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {isExpanded ? '▼' : '▶'}
        </td>
        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#17a2b8', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {ord.orderNo || ord.id}
        </td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {ord.timestampDateTime || ord.timestamp ? moment(ord.timestampDateTime || ord.timestamp).format('HH:mm') : '-'}
        </td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {ord.bookingInfo?.seatNumber || ord.customerInfo?.seatNumber || '-'}
        </td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {ord.bookingInfo?.pnr || ord.customerInfo?.pnr || '-'}
        </td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          <span className="status-consolidate margLeft00" style={{
            backgroundColor: getPaymentTypeColor(payType),
            padding: '4px 14px',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            display: 'inline-block'
          }}>{payType.toLowerCase() === 'none' ? 'SSR' : payType}</span>
        </td>
        <td style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
          {currency} {grandTotal.toFixed(2)}
        </td>
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{itemsCount} item{itemsCount > 1 ? 's' : ''}</td>
      </tr>

      {isExpanded && (
        <ExpandedOrderDetails
          ord={ord}
          orderDetails={orderDetails}
          currency={currency}
          cardPayments={cardPayments}
          themedColor={themedColor}
        />
      )}
    </React.Fragment>
  );
};

const OrdersTab = ({ orderDetails, countryDetails, expandedOrderNo, setExpandedOrderNo, themedColor }) => {
  const orders = orderDetails?.orders;
  if (!orders || orders.length === 0) {
    return (
      <p style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
        No orders recorded for this employee on this sector.
      </p>
    );
  }

  return (
    <>
      <div className='table-header'></div>
      <div className="flexCol width100" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        <table style={{ borderRadius: '5px', width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#1c2b40' }}>
            <tr className='sticky' style={{ textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', width: '35px', borderRight: '1px solid rgba(255,255,255,0.08)' }}></th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Order No</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Time</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Seat No</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>PNR</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Payment Method</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Total Amount</th>
              <th style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>Items</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((ord, idx) => (
              <OrderRow
                key={idx}
                ord={ord}
                idx={idx}
                isExpanded={expandedOrderNo === (ord.orderNo || ord.id)}
                setExpandedOrderNo={setExpandedOrderNo}
                orderDetails={orderDetails}
                countryDetails={countryDetails}
                themedColor={themedColor}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const ItemsTab = ({ orderDetails, countryDetails }) => {
  const itemsSold = orderDetails?.itemsSold;
  if (!itemsSold || itemsSold.length === 0) {
    return (
      <p style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
        No products sold by this employee on this sector.
      </p>
    );
  }

  const currency = countryDetails?.currency?.symbol || '₹';

  return (
    <>
      <div className='table-header'></div>
      <div className="flexCol width100" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        <table style={{ borderRadius: '5px', width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#1c2b40' }}>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Item Code</th>
              <th style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Item Name</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Unit Price</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Qty Sold</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {itemsSold.map((prod, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>{prod.itemCode}</td>
                <td style={{ padding: '10px 12px', fontWeight: 500, borderRight: '1px solid rgba(255,255,255,0.08)' }}>{prod.itemName}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)' }}>{currency} {prod.unitPrice || 0}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, borderRight: '1px solid rgba(255,255,255,0.08)' }}>{prod.totalQuantity || 0}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#39cc6c', fontWeight: 600 }}>{currency} {prod.totalAmount ? prod.totalAmount.toFixed(2) : '0.00'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const CrewTab = ({ activeCrewModalItem }) => {
  const cabinCrew = activeCrewModalItem?.cabinCrew;
  if (!cabinCrew || cabinCrew.length === 0) {
    return (
      <p style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
        No crew details recorded for this sector.
      </p>
    );
  }

  return (
    <>
      <div className='table-header'></div>
      <div className="flexCol width100" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        <table style={{ borderRadius: '5px', width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#1c2b40' }}>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Name</th>
              <th style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Employee Code</th>
              <th style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>Role</th>
              <th style={{ padding: '10px 12px' }}>Position</th>
            </tr>
          </thead>
          <tbody>
            {cabinCrew.map((crew, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 500, borderRight: '1px solid rgba(255,255,255,0.08)' }}>{crew.name || crew.employeeName || '-'}</td>
                <td style={{ padding: '10px 12px', borderRight: '1px solid rgba(255,255,255,0.08)' }}>{crew.employeeCode || '-'}</td>
                <td style={{ padding: '10px 12px', color: '#17a2b8', borderRight: '1px solid rgba(255,255,255,0.08)' }}>{crew.role || '-'}</td>
                <td style={{ padding: '10px 12px', color: '#39cc6c', fontWeight: 600 }}>{crew.position || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const RosterDetailsView = ({
  modalTab,
  activeCrewModalItem,
  orderDetails,
  loadingOrderDetails,
  countryDetails,
  crewName
}) => {
  const [expandedOrderNo, setExpandedOrderNo] = useState(null);
  const themeContext = useContext(ThemeContext);
  const themedColor = themeContext?.themedColor || { hover: 'rgba(255, 255, 255, 0.05)' };

  return (
    <div className="flexCol width100" style={{ gap: '15px' }}>
      <SubHeader modalTab={modalTab} orderDetails={orderDetails} countryDetails={countryDetails} />

      {loadingOrderDetails && (modalTab === 'orders' || modalTab === 'items') ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <img src={LoadingGIF} className="icon-size-min" alt="Loading" />
          <p style={{ marginTop: '10px', color: '#aaa', fontSize: '0.85rem' }}>Fetching order and sales details...</p>
        </div>
      ) : (
        <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
          {modalTab === 'orders' && (
            <OrdersTab
              orderDetails={orderDetails}
              countryDetails={countryDetails}
              expandedOrderNo={expandedOrderNo}
              setExpandedOrderNo={setExpandedOrderNo}
              themedColor={themedColor}
            />
          )}

          {modalTab === 'items' && (
            <ItemsTab orderDetails={orderDetails} countryDetails={countryDetails} />
          )}

          {modalTab === 'crew' && (
            <CrewTab activeCrewModalItem={activeCrewModalItem} />
          )}
        </div>
      )}
    </div>
  );
};
